import React from 'react';
import numeral from 'numeral';
import moment from 'moment';
import { getBeneficiaryWithApostrophe } from './Review/beneficiaryWithApostrophe';
import { GuidanceTextResponse } from './utils';
import { replaceQuestionText } from 'shared/utils/formatText';
import { Answer } from 'donor/portal/pages/FeedbackDashboard/Feedback/api';

const questionTextBlock = (
  programName: string,
  reportYear: number,
  primaryBeneficiary?: string,
  i?: string,
): { [id: number]: JSX.Element } => {
  const intermediaries = i?.toLowerCase();
  const currentYear = new Date().getFullYear();
  const wrappedProgramName = (
    <span className="program-name">{programName}</span>
  );
  const beneficiaryName = intermediaries || primaryBeneficiary;
  const singularBeneficiaryWithApostrophe = beneficiaryName
    ? getBeneficiaryWithApostrophe(beneficiaryName)
    : 'people’s';
  return {
    40007: (
      <>
        Is developing {singularBeneficiaryWithApostrophe} skills or knowledge a
        key part of your program?
      </>
    ),
    40008: (
      <>
        Does your program hope to change {singularBeneficiaryWithApostrophe}{' '}
        behavior or inspire them to take an action?
      </>
    ),
    40010: (
      <>
        How does {wrappedProgramName} improve the lives of{' '}
        {primaryBeneficiary?.toLowerCase() || 'people'}?
      </>
    ),
    40021: (
      <>
        Select any program or organizational improvements to include in your
        report (optional).
      </>
    ),
    1: (
      <>
        How would you describe {wrappedProgramName} to a new prospective donor?
      </>
    ),
    10001: <>How long has the {wrappedProgramName} program been running?</>,
    10004: (
      <>
        What time period should we use for your {wrappedProgramName} annual
        report?
      </>
    ),
    10006: (
      <>
        When
        {reportYear <= currentYear ? ' did ' : ' will '}
        initial planning and development work for {wrappedProgramName} begin?
      </>
    ),
    10003: (
      <>
        What time period should we use for your {wrappedProgramName} annual
        report?
      </>
    ),
  };
};

interface QuestionTextProps {
  text?: string;
  donationTotal: string | number;
  programName: string;
  sponsorName?: string;
  intermediaries?: string;
  reportStartDate: string;
  reportEndDate: string;
  dbId: number;
  guidanceText: GuidanceTextResponse;
  reportYear: number;
  primaryBeneficiary?: string;
}

/*
  Renders a question replacing the variables, and highlights them,
  if new variables are added, they must be sent as props and replaced.
  text (string) the raw question from database
  donationTotal (number) a variable containing the program donation
  programName (text) a variable containing the program name
*/
const QuestionText: React.FC<QuestionTextProps> = ({
  text,
  guidanceText,
  donationTotal,
  programName,
  sponsorName = '',
  reportYear,
  reportStartDate,
  reportEndDate,
  dbId,
  intermediaries,
  primaryBeneficiary = 'Beneficiaries',
}) => {
  const qt = questionTextBlock(
    programName,
    reportYear,
    primaryBeneficiary,
    intermediaries,
  );
  if (qt[dbId]) {
    return <>{qt[dbId]}</>;
  }

  const baseText = text || '';
  const originalText = baseText.includes('[HELP]')
    ? baseText.split('[HELP]')?.[0]
    : baseText;
  const questionText = originalText
    .replace(/<Donation_total>/g, numeral(donationTotal).format(0, 0))
    .replace(/<Program[_\s]name>/gi, programName)
    .replace(/<Report_year>/gi, reportYear?.toString())
    .replace(/<Sponsor_name>/g, sponsorName)
    .replace(/<Report_start_date>/g, moment(reportStartDate).format('D/M YYYY'))
    .replace(/<Report_end_date>/g, moment(reportEndDate).format('D/M YYYY'))
    .replace(/<Primary_beneficiary_or_Intermediary>/g, primaryBeneficiary);
  return <>{questionText}</>;
};

interface QuestionTextFromAnswersProps {
  questionId: number;
  answer: Answer;
  answersById: { [id: number]: Answer };
  reportYear: string | undefined;
  programName: string;
  guidanceText: GuidanceTextResponse;
  donorName: string;
  reportStartDate: string;
  reportEndDate: string;
}

export const QuestionTextFromAnswers: React.FC<QuestionTextFromAnswersProps> = ({
  donorName,
  questionId,
  answer,
  answersById,
  reportYear,
  reportStartDate,
  reportEndDate,
  programName,
  guidanceText,
}) => {
  const text = answer.question.text;
  const donationTotal = '';
  const dbId = questionId;
  const sponsorName = donorName;
  const primaryBeneficiary = '';
  const donationTotalDisplay = '';
  const programNameAlt = '';
  const reportTense = '';
  const q4Answer = '';
  const q3answerIndicatorDetails = '';
  const q4answerIndicatorDetails = '';
  const q5answerIndicatorDetails = '';
  const serviceExample = '';
  const socialImpactIndicator = '';

  const serviceType = answersById[40006]?.text;
  const serviceGoal = answersById[40003]?.text;
  const intermediaries = answersById[40022]?.text;
  const beneficiaryTerm = answersById[40004]?.text;

  const year = reportYear?.toString();

  return (
    <div>
      {replaceQuestionText({
        text,
        year,
        programName,
        reportStartDate,
        reportEndDate,
        sponsorName,
        serviceGoal,
        primaryBeneficiary: intermediaries || beneficiaryTerm,
      })}
    </div>
  );
};

export default QuestionText;
